/* PB Bundle Ladder (scroll)

   Drives the tiers of sections/pb-bundle-ladder-scroll.liquid from the scroll
   position: a tier lights up once its badge reaches a line in the viewport, and
   the one below it takes over as the visitor keeps scrolling. Reaching the last
   tier ends the story: that tier stays lit and the ladder never falls back to
   its empty rest state.

   The tiers are not tappable — the ladder reads, it does not respond. The one
   exception is the theme editor, which lights the tier whose block the merchant
   selects so they can see what they are editing.

   Deliberately separate from pb-bundle-ladder.js, which owns the timed version:
   each script claims its own ladder attribute, so the two sections can sit on
   one page without fighting over the same rows. */
(function () {
  if (window.PBBundleLadderScroll) {
    window.PBBundleLadderScroll.init(document);
    return;
  }

  var controllers = new WeakMap();

  function findLadders(root) {
    var ladders = [];

    if (root.matches && root.matches('[data-pbll-scroll-ladder]')) ladders.push(root);
    if (root.querySelectorAll) {
      ladders = ladders.concat(
        Array.prototype.slice.call(root.querySelectorAll('[data-pbll-scroll-ladder]'))
      );
    }

    return ladders;
  }

  function createController(ladder) {
    var rows = Array.prototype.slice.call(ladder.querySelectorAll('[data-pbll-row]'));
    if (!rows.length) return null;

    var last = rows.length - 1;
    var wantsScroll = ladder.getAttribute('data-pbll-scroll') === 'true';
    var line = parseInt(ladder.getAttribute('data-pbll-line'), 10);
    if (!line || line < 5 || line > 100) line = 72;

    var step = parseInt(ladder.getAttribute('data-pbll-step'), 10);
    if (!step || step < 40 || step > 600) step = 150;

    /* "both": the tiers keep following the scroll in either direction, and a tap
       pins one until the ladder leaves the screen. "down": the story runs once
       on the way down and then hands over to tapping for good. */
    var bothWays = ladder.getAttribute('data-pbll-direction') !== 'down';

    var stillMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var active = null;
    var frame = 0;
    var observer = null;
    var listening = false;

    /* Scroll driving is not in play at all: the option is off, the visitor asked
       for less motion, or a one-way story has run its course. */
    var off = false;

    /* The last tier has been reached at least once. Keeps the ladder from ever
       falling back to the empty rest state. */
    var storyDone = false;

    /* The theme editor asked for a specific tier, so the scroll stops guessing
       until the ladder is off screen. Nothing on the storefront sets this. */
    var pinned = false;

    function setActive(index) {
      if (index === active) return;
      active = index;

      rows.forEach(function (row, i) {
        row.classList.toggle('is-active', i === index);
      });
    }

    /* Which tier the scroll position is at: the lowest one whose badge has
       passed the trigger line. The badge rather than the row box, because the
       row is as tall as its labels and its centre would drift with the copy;
       the badge is what the visitor reads as "the tier".

       Monotone in the scroll position, so scrolling back up walks the tiers
       back down again instead of jumping. -1 is the rest state, with no tier
       lit, which is where the ladder starts. */
    function activeIndexFor(viewport) {
      /* Nothing below the section can be scrolled into view any more, so the
         story has to be finished here — otherwise a ladder close to the end of
         the document could never reach its last tier. */
      var scrolled = window.pageYOffset || document.documentElement.scrollTop || 0;
      var page = document.documentElement.scrollHeight;
      if (scrolled + viewport >= page - 2) return last;

      var centres = [];

      for (var i = 0; i < rows.length; i++) {
        var badge = rows[i].querySelector('[data-pbll-badge]') || rows[i];
        var rect = badge.getBoundingClientRect();

        centres.push(rect.top + rect.height / 2);
      }

      /* Pace. The tiers sit a fixed distance apart — 98px in this design — so
         left alone the whole story would run in one short flick, with the second
         tier arriving almost on top of the first. Each tier past the first
         therefore waits for `step` px of scrolling instead of its own pitch, by
         moving its trigger line up by the difference.

         The pitch is measured rather than assumed, so the pace stays right if
         the row spacing changes. Never above a sixth of the viewport: past that
         a tier would have to light up off screen, and there the tiers simply
         share the topmost line again. */
      var edge = viewport * (line / 100);
      var pitch = centres.length > 1 ? centres[1] - centres[0] : 0;
      var extra = pitch > 0 ? Math.max(0, step - pitch) : 0;
      var ceiling = viewport * 0.15;
      var index = -1;

      for (var j = 0; j < centres.length; j++) {
        if (centres[j] <= Math.max(ceiling, edge - j * extra)) index = j;
      }

      return index;
    }

    function update() {
      frame = 0;
      if (off || pinned) return;

      var viewport = window.innerHeight || document.documentElement.clientHeight || 0;
      if (!viewport) return;

      var index = activeIndexFor(viewport);

      /* Once the story has run, never drop back to the empty rest state on the
         way up: a ladder with nothing lit reads as broken to someone who has
         already seen it fill in. */
      if (storyDone && index < 0) index = 0;

      setActive(index);

      if (index >= last && !storyDone) {
        storyDone = true;

        /* One-way: the story played once and the ladder rests on its last tier. */
        if (!bothWays) stop();
      }
    }

    function onScroll() {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    }

    function startListening() {
      if (listening || off) return;
      listening = true;

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    }

    function stopListening() {
      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }

      if (!listening) return;
      listening = false;

      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    /* Hands the ladder over for good: no more scroll driving, nothing left
       observing. Only the one-way mode and the fallbacks end up here. */
    function stop() {
      off = true;
      stopListening();

      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }

    /* Editor only: light the tier whose block is selected and hold it there, so
       the merchant sees what they are editing instead of whatever the scroll
       position happens to say. Released when the ladder leaves the screen. */
    function selectRow(row) {
      var index = rows.indexOf(row);
      if (index === -1) return;

      pinned = true;
      storyDone = true;
      setActive(index);
    }

    function destroy() {
      stopListening();

      if (observer) {
        observer.disconnect();
        observer = null;
      }

      controllers.delete(ladder);
    }

    var canScrollDrive =
      wantsScroll &&
      !stillMotion &&
      'IntersectionObserver' in window &&
      'requestAnimationFrame' in window;

    if (!canScrollDrive) {
      off = true;
      storyDone = true;
      setActive(last);
    } else {
      setActive(-1);

      /* The scroll handler only runs while the ladder is somewhere on screen,
         so scrolling the rest of the page costs nothing. Leaving the screen is
         also what releases a tier the editor pinned, so a reader who scrolls
         back gets the scroll story rather than a merchant's last selection. */
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              startListening();
            } else {
              stopListening();
              pinned = false;
            }
          });
        },
        { threshold: 0 }
      );
      observer.observe(ladder);
    }

    return {
      destroy: destroy,
      selectRow: selectRow,
    };
  }

  function init(root) {
    findLadders(root).forEach(function (ladder) {
      if (controllers.has(ladder)) return;

      var controller = createController(ladder);
      if (controller) controllers.set(ladder, controller);
    });
  }

  function destroy(root) {
    findLadders(root).forEach(function (ladder) {
      var controller = controllers.get(ladder);
      if (controller) controller.destroy();
    });
  }

  window.PBBundleLadderScroll = {
    init: init,
    destroy: destroy,
  };

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var row = event.target.closest ? event.target.closest('[data-pbll-row]') : null;
    var ladder = row && row.closest('[data-pbll-scroll-ladder]');
    var controller = ladder && controllers.get(ladder);

    if (controller) controller.selectRow(row);
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        init(document);
      },
      { once: true }
    );
  } else {
    init(document);
  }
})();
